import { GridApi } from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  model,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ProtocolSectionComponent } from '@pages/investigator/components/protocol-section/protocol-section.component';

import { ToggleCurrencyComponent } from '@widgets/toggle-currency/toggle-currency.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { PatientGroupsModel } from '@models/patient-group/patient-groups.model';
import { PatientGroupsQuery } from '@models/patient-group/patient-groups.query';
import { PatientGroupsService } from '@models/patient-group/patient-groups.service';
import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { SitesStore } from '@models/sites/sites.store';

import { ButtonToggleItem } from '@shared/components/button-toggle-group/button-toggle-item.model';
import { MessagesConstants } from '@shared/constants/messages.constants';
import {
  EntityType,
  EventType,
  PatientGroupType,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { PaymentSchedulesQuery } from '@shared/store/payment-schedules/payment-schedules.query';
import { PaymentSchedulesService } from '@shared/store/payment-schedules/payment-schedules.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { CurrencyToggle, Option } from '@shared/types/components.type';
import { ExportType, Utils } from '@shared/utils/utils';

import { MultipleSiteSelectComponent } from './components/multiple-site-select/multiple-site-select.component';
import { PatientProtocolActionsComponent } from './components/patient-protocol-actions/patient-protocol-actions.component';
import { SiteCostSummaryTableComponent } from './components/site-cost-summary-table/site-cost-summary-table.components';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProtocolSectionComponent,
    WorkflowPanelComponent,
    ExportExcelButtonComponent,
    AsyncPipe,
    SiteCostSummaryTableComponent,

    PatientProtocolActionsComponent,
    ReactiveFormsModule,
    ToggleCurrencyComponent,
    MultipleSiteSelectComponent,
  ],
  selector: 'aux-patient-protocol',
  templateUrl: './patient-protocol.component.html',
})
export class PatientProtocolComponent implements OnDestroy {
  eventQuery = inject(EventQuery);
  sitesQuery = inject(SitesQuery);

  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientGroupsQuery = inject(PatientGroupsQuery);
  private patientGroupsService = inject(PatientGroupsService);
  private patientProtocolService = inject(PatientProtocolService);
  private paymentSchedulesQuery = inject(PaymentSchedulesQuery);
  private paymentSchedulesService = inject(PaymentSchedulesService);
  private sitesService = inject(SitesService);
  private sitesStore = inject(SitesStore);
  private workflowQuery = inject(WorkflowQuery);

  selectedCostType = signal<SiteCostType>(SiteCostType.PATIENT_VISITS);
  selectedCurrency = signal<CurrencyToggle>(CurrencyToggle.PRIMARY);
  selectedPatientGroup = signal<null | string>(null);
  selectedProtocolVersion = signal<null | string>(null);
  selectedSiteIds = signal<string[]>([]);

  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  patientTrackerLockedTooltip = computed(() =>
    this.isPatientsFinalized() ? MessagesConstants.PATIENT_TRACKER_CLOSED : ''
  );

  selectedSiteIDs = model<string[]>([]);

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  gridAPI!: GridApi;
  hideGrid$ = new BehaviorSubject(false);
  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  importSiteCostFF$ = new BehaviorSubject(false);
  isClosingPanelEnabled = this.launchDarklyService.$select(
    (flags) => flags.closing_checklist_toolbar
  );
  isProtocolVersionLoading$ = new BehaviorSubject(true);
  isQuarterCloseEnabled = this.workflowQuery.isWorkflowAvailable;
  patientGroupOptions: ButtonToggleItem[] = [];
  patientGroups: PatientGroupsModel[] = [];
  patientGroupsLoading$ = new BehaviorSubject(false);
  patientSchedulesLoading$ = this.paymentSchedulesQuery.selectLoading();
  protocolForm!: FormGroup;
  protocolVersionOptions: Option[] = [];
  siteIdsFc = new FormControl<string[]>([]);
  userHasLockInvestigatorDataPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_CHECKLIST_PATIENT_DATA],
  });
  userHasUpdateSiteCostsPermission$ = this.authService.isAuthorized$({
    permissions: [PermissionType.PERMISSION_UPDATE_SITE_COSTS],
    sysAdminsOnly: false,
  });
  visitCostsFF$ = new BehaviorSubject(false);
  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER;

  ngOnDestroy() {
    this.sitesStore.setActive([]);
    this.paymentSchedulesService.cache.clear();
  }

  constructor() {
    this.sitesStore.setActive([]);

    this.sitesService
      .get()
      .pipe(takeUntilDestroyed())
      .subscribe((sites) => {
        const sortedSites = sites?.data?.sort(({ site_no }, { site_no: site_no2 }) =>
          Utils.localeAlphaNumSort(site_no || '', site_no2 || '')
        );

        this.siteIdsFc.setValue(sortedSites?.length ? [sortedSites[0].id] : []);
        this.selectedSiteIDs.set(this.siteIdsFc.value || []);
      });

    this.launchDarklyService.flags$.pipe(takeUntilDestroyed()).subscribe((flags) => {
      this.importSiteCostFF$.next(flags.import_site_cost_data);
      this.visitCostsFF$.next(flags.visit_costs);
    });

    combineLatest([
      this.mainQuery
        .select('trialKey')
        .pipe(switchMap(() => this.patientProtocolService.getPatientProtocolVersions()))
        .pipe(tap(() => this.isProtocolVersionLoading$.next(true))),
      this.mainQuery.select('trialKey').pipe(tap(() => this.patientGroupsLoading$.next(true))),
      this.patientGroupsService.get([PatientGroupType.PATIENT_GROUP_STANDARD]),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([versions]) => {
        setTimeout(() => {
          this.isProtocolVersionLoading$.next(false);
          this.protocolVersionOptions = versions.map(({ id, name }) => ({
            label: name,
            value: id,
          }));

          this.patientGroupsLoading$.next(false);
          this.patientGroupOptions = [];
          this.patientGroups = [];
          this.patientGroupsQuery
            .getAll()
            .forEach((patientGroup) => this.patientGroups.push(patientGroup));
          this.patientGroupOptions.push(...this.getProtocolGroupOptions(this.patientGroups));

          this.setDefaultProtocolFormValues();
        }, 0);
      });
  }

  getProtocolGroupOptions(patientGroups: PatientGroupsModel[]) {
    const options: { label: string; show: Observable<boolean>; value: string }[] = [];

    patientGroups.forEach((group) => {
      options.push({ label: group.name, show: of(true), value: group.id });
    });

    if (this.protocolVersionOptions.length) {
      options.push(
        {
          label: 'Visit Costs',
          show: this.launchDarklyService.select$((flags) => flags.visit_costs),
          value: 'visits_costs',
        },
        { label: 'Invoiceables', show: of(true), value: 'invoiceables' }
      );
    }

    return options;
  }

  handleGridApi(gridApi: GridApi) {
    this.gridAPI = gridApi;
  }

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  onApplySelection(siteIds: string[]) {
    this.siteIdsFc.setValue(siteIds);
  }

  onExportPatientBudget = async () => {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: this.protocolForm.controls.protocolVersion.value,
          export_type: ExportType.PATIENT_BUDGET,
          filename: `${trialName}_Site_Cost_Summary_${dateStr}`,
          json_properties: {
            cost_type: this.selectedCostType(),
            patient_group_id:
              this.selectedPatientGroup() === 'visits_costs' ? null : this.selectedPatientGroup(),
            site_ids: this.selectedSiteIDs(),
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  };

  onProtocolFormReady(form: FormGroup) {
    this.protocolForm = form;

    this.setDefaultProtocolFormValues();

    this.protocolForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ patientGroup, protocolVersion, siteBudgetCostType }) => {
        this.selectedCostType.set(siteBudgetCostType);
        this.selectedPatientGroup.set(patientGroup ?? null);
        this.selectedProtocolVersion.set(protocolVersion);
      });
  }

  private setDefaultProtocolFormValues() {
    if (this.protocolForm) {
      setTimeout(() => {
        this.protocolForm.patchValue({
          protocolVersion: this.protocolVersionOptions[0]?.value || null,
          siteBudgetCostType: SiteCostType.PATIENT_VISITS,
        });
      }, 0);
    }
  }
}
